import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useTRPC } from '@/utils/trpc';

/**
 * Thin wrapper around testResult.delete for use within the unified
 * assessment results delete hook. Invalidates both test-result and
 * assessment-level queries so that mixed-type pages refresh properly.
 */
export const useDeleteTestResultsForAssessmentsTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.testResult.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResults.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.resultsRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    deleteTestResults: async (ids: string[]): Promise<void> => {
      await mutation.mutateAsync({ ids });
    },
    isLoading: mutation.isPending,
  };
};
